/**
 * W4A — Bridge-mediated frontend-session token hook (WI 4858 sub-plan 4).
 *
 * # What this does
 * Fetches the audience-restricted FE-session token from the host bridge
 * (resource URI: `"ethisys:session-token"`) and manages silent refresh.
 * The token is delivered to this hook; it is NEVER the capability JWT
 * (that stays brokered host-side and never reaches plugin JS).
 *
 * # Refresh policy
 * Refresh fires 30 s before `expiresAtUtc`. The timer is cleared on
 * unmount; no stale async operations survive component teardown.
 *
 * # Transport
 * The hook is transport-agnostic — it accepts any {@link McpTransport}.
 * Pass the context transport in production; a mock in tests.
 *
 * This is the canonical FE-session-token hook; `@ethisyscore/plugin-ui/token`
 * re-exports it so plugin authors take a single dependency on plugin-ui.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import type { McpTransport } from "./transport";

const SESSION_TOKEN_URI = "ethisys:session-token";
const REFRESH_SKEW_MS = 30_000; // refresh 30 s before expiry

interface FrontendSessionTokenPayload
{
    accessToken: string;
    expiresAtUtc: string; // ISO-8601
    jti: string;
}

export interface UseFrontendSessionTokenResult
{
    /** The compact-serialised JWT, or null while loading / on error. */
    token: string | null;
    /** True while the first fetch (or a refresh) is in-flight. */
    isLoading: boolean;
    /** Set when the bridge rejects; null otherwise. */
    error: Error | null;
}

/**
 * Returns the current FE-session token fetched from the host bridge.
 * Schedules silent refresh 30 s before the token expires.
 *
 * @param transport The {@link McpTransport} to use. Typically the context
 * transport from the extension runtime provider.
 */
export function useFrontendSessionToken(
    transport: McpTransport,
): UseFrontendSessionTokenResult
{
    const [token, setToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const mountedRef = useRef(true);

    const fetch = useCallback(async (): Promise<void> =>
    {
        if (refreshTimerRef.current !== null)
        {
            clearTimeout(refreshTimerRef.current);
            refreshTimerRef.current = null;
        }
        try
        {
            const { data } = await transport.getResource<FrontendSessionTokenPayload>(
                SESSION_TOKEN_URI,
            );

            if (!mountedRef.current)
            {
                return;
            }

            setToken(data.accessToken);
            setError(null);
            setIsLoading(false);

            // Schedule refresh 30 s before expiry.
            const expiresAtMs = Date.parse(data.expiresAtUtc);
            const refreshIn = expiresAtMs - Date.now() - REFRESH_SKEW_MS;
            if (refreshIn > 0)
            {
                refreshTimerRef.current = setTimeout(() =>
                {
                    void fetch();
                }, refreshIn);
            }
        }
        catch (err)
        {
            if (!mountedRef.current)
            {
                return;
            }
            setError(err instanceof Error ? err : new Error(String(err)));
            setToken(null);
            setIsLoading(false);
        }
    }, [transport]);

    useEffect(() =>
    {
        mountedRef.current = true;
        setIsLoading(true);
        void fetch();

        return (): void =>
        {
            mountedRef.current = false;
            if (refreshTimerRef.current !== null)
            {
                clearTimeout(refreshTimerRef.current);
            }
        };
    }, [fetch]);

    return { token, isLoading, error };
}
